import {
  Col,
  Row,
  Input,
  List,
  Typography,
  Tag,
  Avatar,
  Spin,
  Empty,
  message,
  Tooltip,
  Select,
  Divider,
  Button,
  Modal,
  Badge,
  Space,
  Steps,
  Descriptions,
  Segmented,
} from "antd"
import React, { useState, useMemo, useEffect } from "react"
import {
  SearchOutlined,
  FileTextOutlined,
  BugOutlined,
  ShoppingCartOutlined,
  QuestionOutlined,
  BulbOutlined,
  SendOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  FileOutlined,
} from "@ant-design/icons"

import { useAuthContext } from "../../hooks/useAuthContext"
import useFetchTickets from "../../store/Tickets/useTicketsFetch"
import PageHeader from "../Elements/PageHeader"
import { TbTicket } from "react-icons/tb"
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore"
import { db } from "../../firebase/config"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import duration from "dayjs/plugin/duration"
import { useCollection } from "../../hooks/useCollection"
import PaperManagerOrderOptions from "./PaperManagerOrderOptions"
import { formatNumberWithSpaces } from "../Orders/Helpers/FormatNumbersWithSpaces"
import InvoiceDetails from "./InvoiceDetails"

dayjs.extend(relativeTime)
dayjs.extend(duration)

const { Text, Title, Paragraph } = Typography

const blockstyle = {
  background: "white",
  borderRadius: 15,
  padding: 25,
  width: "100%",
  height: "calc(90vh - 150px)",
  display: "flex",
  flexDirection: "column",
}

const listStyle = {
  flexGrow: 1,
  overflowY: "auto",
  marginTop: "20px",
}

// Helper to get an icon based on ticket type
const getTicketIcon = (type) => {
  switch (type) {
    case "Software Bug":
      return <BugOutlined />
    case "Order":
      return <ShoppingCartOutlined />
    case "Question":
      return <QuestionOutlined />
    case "Other":
      return <BulbOutlined />
    case "Invoice":
      return <FileOutlined />
    default:
      return <TbTicket />
  }
}
const getTicketColor = (type) => {
  switch (type) {
    case "Software Bug":
      return "#f5222d" // red
    case "Order":
      return "#1677ff" // blue
    case "Question":
      return "#13c2c2" // cyan
    case "Other":
      return "#faad14" // orange
    case "Invoice":
      return "#eb2f96" // orange
    default:
      return "#8c8c8c" // grey
  }
}

export default function Tickets() {
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [newMessage, setNewMessage] = useState("")
  const [sending, setSending] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [usersToAssign, setUsersToAssign] = useState([])
  const [statusFilter, setStatusFilter] = useState("all")
  const [timeInProgress, setTimeInProgress] = useState("")
  const [orderDetails, setOrderDetails] = useState(null)
  const [orderLoading, setOrderLoading] = useState(false)
  const [view, setView] = useState("Active")
  const [closedTickets, setClosedTickets] = useState([])
  const [closedLoading, setClosedLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const [invoiceDetails, setInvoiceDetails] = useState(null)

  const { data: allUsers } = useCollection("users")
  const { user, paperManager, admin } = useAuthContext()

  const { tickets, loading: ticketsLoading } = useFetchTickets()

  console.log(admin)

  const sortedAndFilteredTickets = useMemo(() => {
    const statusPriority = {
      open: 1,
      "awaiting-response": 2,
      "in-progress": 3,
      closed: 4,
    }
    const sorted = [...tickets].sort((a, b) => {
      const priorityA = statusPriority[a.status] || 5
      const priorityB = statusPriority[b.status] || 5
      return priorityA - priorityB
    })
    const byStatus =
      statusFilter === "all" ? sorted : sorted.filter((ticket) => ticket.status === statusFilter)
    if (!searchTerm) {
      return byStatus
    }
    return byStatus.filter(
      (ticket) =>
        (ticket.ticketId && ticket.ticketId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (ticket.description && ticket.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [searchTerm, tickets, statusFilter])

  const handleDeletionResponse = async (messageId, response) => {
    if (!selectedTicket || !user) return
    setActionLoading(true)

    const batch = writeBatch(db)
    const ticketRef = doc(db, "tickets", selectedTicket.id)
    const orderRef = doc(db, "orders", selectedTicket.orderId)

    const updatedMessages = selectedTicket.messages.map((msg) => {
      if (msg.id === messageId) {
        return { ...msg, response }
      }
      return msg
    })

    const systemMessageText =
      response === "accepted"
        ? `Deletion request was APPROVED by ${user.email}.`
        : `Deletion request was DECLINED by ${user.email}.`

    const systemMessage = {
      id: Timestamp.now().toMillis().toString(),
      text: systemMessageText,
      senderEmail: "System",
      timestamp: Timestamp.now(),
      type: "system",
    }

    if (response === "accepted") {
      // Update the order status to 999 (STOPPED) when the request is accepted.
      batch.update(orderRef, { orderStatus: 999 })

      const closedTicketData = {
        ...selectedTicket,
        messages: [...updatedMessages, systemMessage],
        status: "closed",
        closedAt: Timestamp.now(),
      }
      const closedTicketRef = doc(db, "closedTickets", selectedTicket.id)
      batch.set(closedTicketRef, closedTicketData)
      batch.delete(ticketRef)
    } else {
      // If declined, set order status to 998 (On Hold)
      batch.update(orderRef, { orderStatus: 998 })
      batch.update(ticketRef, {
        messages: [...updatedMessages, systemMessage],
        status: "in-progress",
      })
    }

    try {
      await batch.commit()
      message.success(`Request successfully ${response}.`)
      if (response === "accepted") setSelectedTicket(null)
    } catch (error) {
      console.error("Error responding to request:", error)
      message.error("Failed to process the response.")
    } finally {
      setActionLoading(false)
    }
  }

  useEffect(() => {
    if (view !== "Finished" || !user) {
      setClosedTickets([])
      return
    }

    setClosedLoading(true)
    const closedTicketsRef = collection(db, "closedTickets")
    const q = query(closedTicketsRef, where("assignedToId", "array-contains", user.uid))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const closedList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        setClosedTickets(closedList)
        setClosedLoading(false)
      },
      (error) => {
        console.error("Error fetching closed tickets:", error)
        message.error("Could not load finished tickets.")
        setClosedLoading(false)
      }
    )

    return () => unsubscribe()
  }, [view, user])

  useEffect(() => {
    // Get stable primitive values from the selected ticket
    const ticketOrderId = selectedTicket?.orderId
    const ticketInvoiceId = selectedTicket?.invoiceId

    const fetchContextData = async () => {
      setOrderLoading(true)
      // Always reset both details when the effect runs
      setOrderDetails(null)
      setInvoiceDetails(null)

      try {
        if (ticketOrderId) {
          const orderRef = doc(db, "orders", ticketOrderId)
          const orderSnap = await getDoc(orderRef)
          setOrderDetails(orderSnap.exists() ? { id: orderSnap.id, ...orderSnap.data() } : null)
        } else if (ticketInvoiceId) {
          const invoiceRef = doc(db, "invoices", ticketInvoiceId)
          const invoiceSnap = await getDoc(invoiceRef)
          setInvoiceDetails(
            invoiceSnap.exists() ? { id: invoiceSnap.id, ...invoiceSnap.data() } : null
          )
        }
      } catch (error) {
        console.error("Error fetching context details:", error)
        message.error("Failed to load item details.")
      } finally {
        setOrderLoading(false)
      }
    }

    // Only fetch if one of the IDs exists
    if (ticketOrderId || ticketInvoiceId) {
      fetchContextData()
    } else {
      // If no ticket is selected (or it has no associated items), ensure details are cleared.
      setOrderDetails(null)
      setInvoiceDetails(null)
    }
  }, [selectedTicket?.orderId, selectedTicket?.invoiceId])

  useEffect(() => {
    if (selectedTicket?.status !== "in-progress") {
      setTimeInProgress("")
      return
    }

    const calculateDuration = () => {
      const startTime = dayjs(
        selectedTicket.inProgressAt?.toDate() || selectedTicket.createdAt.toDate()
      )
      const diff = dayjs().diff(startTime)

      const d = dayjs.duration(diff)
      return `${Math.floor(d.asDays())}d ${d.hours()}h ${d.minutes()}m ${d.seconds()}s`
    }

    setTimeInProgress(calculateDuration())
    const intervalId = setInterval(() => setTimeInProgress(calculateDuration()), 1000)

    return () => clearInterval(intervalId)
  }, [selectedTicket])

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || !user) return
    setSending(true)

    const ticketRef = doc(db, "tickets", selectedTicket.id)
    const messageObject = {
      id: `${Timestamp.now().toMillis()}-${user.uid}`,
      text: newMessage,
      senderId: user.uid,
      senderEmail: user.email,
      timestamp: Timestamp.now(),
    }

    try {
      await updateDoc(ticketRef, { messages: arrayUnion(messageObject) })
      setNewMessage("")
    } catch (error) {
      console.error("Error sending message:", error)
      message.error("Failed to send message.")
    } finally {
      setSending(false)
    }
  }
  useEffect(() => {
    setSelectedTicket(null)
  }, [view])

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return

    if (newStatus === "closed") {
      // ✅ ADMIN BYPASS: ignore all logic, close + send order to location 999
      if (admin) {
        const batch = writeBatch(db)

        const originalTicketRef = doc(db, "tickets", selectedTicket.id)
        const closedTicketRef = doc(db, "closedTickets", selectedTicket.id)

        // If linked order exists -> set orderLocation to 999
        if (selectedTicket.orderId) {
          const orderRef = doc(db, "orders", selectedTicket.orderId)
          batch.update(orderRef, { orderLocation: 999 })
        }

        const closedTicketData = {
          ...selectedTicket,
          status: "closed",
          closedAt: Timestamp.now(),
          adminForcedClose: true,
          closedBy: user?.email || null,
          closedById: user?.uid || null,
        }

        batch.set(closedTicketRef, closedTicketData)
        batch.delete(originalTicketRef)

        try {
          await batch.commit()
          message.success(
            `Admin closed ticket ${selectedTicket.ticketId}. Order moved to location 999.`
          )
          setSelectedTicket(null)
        } catch (error) {
          console.error("Error (admin) closing ticket:", error)
          message.error("Failed to close the ticket (admin).")
        }

        return
      }

      // ✅ NON-ADMIN: keep your existing logic EXACTLY as it is
      if (orderDetails && [997, 998].includes(orderDetails.orderStatus)) {
        message.error(
          "Cannot close ticket: The associated order is On Hold or marked for deletion."
        )
        return
      }

      const batch = writeBatch(db)
      const originalTicketRef = doc(db, "tickets", selectedTicket.id)
      const closedTicketRef = doc(db, "closedTickets", selectedTicket.id)
      const closedTicketData = { ...selectedTicket, status: "closed", closedAt: Timestamp.now() }

      batch.set(closedTicketRef, closedTicketData)
      batch.delete(originalTicketRef)

      try {
        await batch.commit()
        message.success(`Ticket ${selectedTicket.ticketId} has been closed and archived.`)
        setSelectedTicket(null)
      } catch (error) {
        console.error("Error closing ticket:", error)
        message.error("Failed to close the ticket.")
      }

      return
    }

    // Everything else unchanged
    const ticketRef = doc(db, "tickets", selectedTicket.id)
    const updateData = { status: newStatus }

    if (newStatus === "in-progress") {
      updateData.inProgressAt = Timestamp.now()
    }

    try {
      await updateDoc(ticketRef, updateData)
      message.success("Ticket status updated.")
    } catch (error) {
      console.error("Error updating status:", error)
      message.error("Failed to update status.")
    }
  }

  const showAssignModal = () => {
    if (selectedTicket) {
      setUsersToAssign(selectedTicket.assignedToId || [])
      setIsModalVisible(true)
    }
  }

  const handleAssignUsers = async () => {
    if (!selectedTicket || !allUsers) return

    // A write batch ensures both documents are updated together or not at all
    const batch = writeBatch(db)

    // --- 1. Update the Ticket ---
    const ticketRef = doc(db, "tickets", selectedTicket.id)
    const selectedUserIds = usersToAssign
    const finalUserIds = new Set([selectedTicket.createById, ...selectedUserIds])
    const userEmailMap = new Map(allUsers.map((u) => [u.id, u.email]))
    const finalIdsArray = Array.from(finalUserIds)
    const finalEmailsArray = finalIdsArray.map((id) => userEmailMap.get(id)).filter(Boolean)

    batch.update(ticketRef, {
      assignedToId: finalIdsArray,
      assignedTo: finalEmailsArray,
    })

    // --- 2. Update the Order (if it exists) ---
    if (selectedTicket.orderId) {
      const orderRef = doc(db, "orders", selectedTicket.orderId)
      batch.update(orderRef, {
        holdOrderAssignedIds: finalIdsArray, // Add the same array to the order
      })
    }

    // --- 3. Commit both changes ---
    try {
      await batch.commit()
      message.success("Assignments updated successfully.")
      setIsModalVisible(false)
    } catch (error) {
      console.error("Error assigning users:", error)
      message.error("Failed to update assignments.")
    }
  }

  useEffect(() => {
    if (selectedTicket?.id) {
      const updatedTicket = tickets.find((t) => t.id === selectedTicket.id)
      if (updatedTicket) {
        setSelectedTicket(updatedTicket)
      }
    } else {
      const currentList = view === "Active" ? tickets : closedTickets
      if (currentList.length > 0) {
        setSelectedTicket(currentList[0])
      }
    }
  }, [tickets, closedTickets, view, selectedTicket])

  const renderOrderStatusTag = (status) => {
    switch (status) {
      case 997:
        return <Tag color="magenta">To be Deleted</Tag>
      case 998:
        return <Tag color="orange">On Hold</Tag>
      case 999:
        return <Tag color="red">STOPPED</Tag>
      default:
        return <Tag color="green">In Production</Tag>
    }
  }

  // --- CHANGE 2: Helper function for Steps component ---
  const getCurrentStep = (status) => {
    switch (status) {
      case "open":
        return 0
      case "awaiting-response":
      case "in-progress":
        return 1
      case "closed":
        return 2
      default:
        return 0
    }
  }

  const getCorrectTag = (status) => {
    switch (status) {
      case "open":
        return <Tag color="#fa541c">{status}</Tag>
      case "awaiting-response":
        return (
          <Tag color="#2f54eb">
            <Space>
              <LoadingOutlined />
              {status}
            </Space>
          </Tag>
        )
      case "in-progress":
        return <Tag color="#52c41a">{status}</Tag>
      case "closed":
        return <Tag color="#f5222d">{status}</Tag>
      default:
        return <Tag color="#8c8c8c">{status}</Tag>
    }
  }

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Space direction="vertical">
            <PageHeader text="Tickets" icon={<FileTextOutlined />} />
            <Segmented
              options={["Active", "Finished"]}
              value={view}
              onChange={setView}
              style={{ marginTop: "10px" }}
            />
          </Space>
        </Col>
        <Col span={6}>
          {/* ... Ticket List Column ... */}
          <div style={blockstyle}>
            <Title level={4}>Ticket list</Title>
            <Paragraph type="secondary">Search or select tickets</Paragraph>
            <Space>
              <Input
                placeholder="Search tickets by ID..."
                prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
              <Select value={statusFilter} style={{ width: 140 }} onChange={setStatusFilter}>
                <Select.Option value="all">All</Select.Option>
                <Select.Option value="open">Open</Select.Option>
                <Select.Option value="in-progress">In Progress</Select.Option>
                <Select.Option value="awaiting-response">Awaiting Response</Select.Option>
              </Select>
            </Space>

            {ticketsLoading || closedLoading ? (
              <div
                style={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <Spin size="large" />
              </div>
            ) : (
              <List
                style={listStyle}
                itemLayout="horizontal"
                dataSource={view === "Active" ? sortedAndFilteredTickets : closedTickets}
                locale={{ emptyText: <Empty description="No tickets found." /> }}
                renderItem={(item) => (
                  <List.Item
                    onClick={() => setSelectedTicket(item)}
                    style={{
                      padding: "12px 8px",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: "pointer",
                      alignItems: "flex-start",
                      backgroundColor: selectedTicket?.id === item.id ? "#e6f4ff" : "transparent",
                    }}>
                    <List.Item.Meta
                      avatar={
                        <Badge
                          dot={item.status === "open"}
                          style={{ width: 12, height: 12 }}
                          color="red"
                          offset={[-2, 28]}>
                          <Avatar
                            shape="circle"
                            icon={getTicketIcon(item.stage)}
                            style={{
                              backgroundColor: getTicketColor(item.stage),
                            }}
                          />
                        </Badge>
                      }
                      title={
                        <Space>
                          <Text strong>{item.ticketId}</Text>
                          {getCorrectTag(item.status)}
                        </Space>
                      }
                      description={
                        <Text ellipsis type="secondary">
                          {item.description}
                        </Text>
                      }
                    />
                    <div style={{ textAlign: "right", marginRight: "10px", paddingTop: "4px" }}>
                      <Text type="secondary">{item.issueType}</Text>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </div>
        </Col>
        <Col span={9}>
          {/* ... Conversation Column ... */}
          <div style={blockstyle}>
            {selectedTicket ? (
              <>
                <Title level={4}>Conversation</Title>
                <Paragraph type="secondary">Communication about the issue</Paragraph>
                <Divider style={{ margin: "10px 0" }} />
                <div
                  className="custom-scrollbar"
                  style={{
                    flexGrow: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column-reverse",
                  }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingRight: "10px",
                      direction: "ltr",
                      background: "#fafafa",
                      borderRadius: 15,
                      padding: 15,
                    }}>
                    {selectedTicket.messages?.map((msg) => {
                      const isDeletionRequest = msg.type === "deletion_request"
                      const canRespond =
                        isDeletionRequest && msg.response === null && user.uid === orderDetails?.uid

                      return (
                        <div
                          key={msg.id}
                          style={{
                            alignSelf: msg.senderId === user.uid ? "flex-end" : "flex-start",
                            backgroundColor:
                              msg.type === "system"
                                ? "#fffbe6"
                                : msg.senderId === user.uid
                                ? "#1677ff"
                                : "#f0f0f0",
                            color:
                              msg.senderId === user.uid && msg.type !== "system"
                                ? "white"
                                : "black",
                            border: msg.type === "system" ? "1px solid #ffe58f" : "none",
                            padding: "8px 14px",
                            borderRadius: "18px",
                            marginBottom: "10px",
                            maxWidth: "80%",
                            wordWrap: "break-word",
                          }}>
                          {msg.senderId !== user.uid && (
                            <Text
                              strong
                              style={{
                                display: "block",
                                fontSize: "12px",
                                marginBottom: "4px",
                                color: "#595959",
                              }}>
                              {msg.senderEmail}
                            </Text>
                          )}
                          <p style={{ marginBottom: "2px", whiteSpace: "pre-wrap" }}>{msg.text}</p>
                          <Space align="end">
                            <Text
                              style={{
                                fontSize: "11px",
                                color:
                                  msg.senderId === user.uid && msg.type !== "system"
                                    ? "#ffffffb3"
                                    : "#888",
                              }}>
                              {dayjs(msg.timestamp.toDate()).fromNow()}
                            </Text>
                            {canRespond && (
                              <Space style={{ marginTop: "10px" }}>
                                <Button
                                  size="small"
                                  type="primary"
                                  danger
                                  icon={<CloseOutlined />}
                                  onClick={() => handleDeletionResponse(msg.id, "declined")}
                                  loading={actionLoading}>
                                  Decline
                                </Button>
                                <Button
                                  size="small"
                                  type="primary"
                                  icon={<CheckOutlined />}
                                  onClick={() => handleDeletionResponse(msg.id, "accepted")}
                                  loading={actionLoading}>
                                  Accept
                                </Button>
                              </Space>
                            )}
                            {isDeletionRequest && msg.response && (
                              <Tag
                                color={msg.response === "accepted" ? "success" : "error"}
                                style={{ marginTop: "8px" }}>
                                {`Responded: ${msg.response.toUpperCase()}`}
                              </Tag>
                            )}
                          </Space>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <Input.Group compact style={{ marginTop: "15px", display: "flex" }}>
                  <Input.TextArea
                    style={{ width: "calc(100% - 50px)" }}
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending || view === "Finished"}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                  />
                  <Button
                    type="primary"
                    onClick={handleSendMessage}
                    disabled={sending || view === "Finished"}
                    icon={<SendOutlined />}
                    style={{ alignSelf: "flex-end" }}
                  />
                </Input.Group>
              </>
            ) : (
              <div style={{ textAlign: "center", margin: "auto" }}>
                <Empty description="Select a ticket to view the conversation." />
              </div>
            )}
          </div>
        </Col>

        <Col span={9}>
          <div style={{ ...blockstyle, overflowY: "auto" }}>
            {selectedTicket ? (
              <>
                <Typography.Title level={4}>Ticket Details</Typography.Title>
                <Steps
                  size="small"
                  current={getCurrentStep(selectedTicket.status)}
                  items={[
                    {
                      title: "Open",
                      description: `${dayjs(selectedTicket.createdAt.toDate()).format(
                        "YYYY-MM-DD"
                      )}`,
                    },
                    {
                      title:
                        selectedTicket.status === "awaiting-response"
                          ? "Awaiting Response"
                          : "In Progress",
                      description: (
                        <Text
                          type={
                            selectedTicket.status === "awaiting-response" ? "warning" : "danger"
                          }>
                          {selectedTicket.status === "in-progress" ? timeInProgress : ""}
                        </Text>
                      ),
                    },
                    {
                      title: "Finished",
                      description:
                        selectedTicket.status === "closed" ? "This ticket is closed." : "",
                    },
                  ]}
                />
                <Divider style={{ margin: "10px 0" }} />

                <Typography.Title level={5} style={{ marginTop: "10px" }}>
                  Ticket Info
                </Typography.Title>

                <p>
                  <Text strong>Status: </Text>
                  <Select
                    size="small"
                    value={selectedTicket.status}
                    disabled={view === "Finished" || selectedTicket.status === "awaiting-response"}
                    onChange={handleStatusChange}
                    style={{ width: 140 }}>
                    <Select.Option value="open">Open</Select.Option>
                    <Select.Option value="in-progress">In Progress</Select.Option>
                    <Select.Option value="awaiting-response" disabled>
                      Awaiting Response
                    </Select.Option>
                    <Select.Option
                      value="closed"
                      disabled={
                        !admin && orderDetails && [997, 998].includes(orderDetails.orderStatus)
                      }>
                      Closed
                    </Select.Option>
                  </Select>
                </p>
                <p>
                  <Text strong>Created By: </Text> {selectedTicket.createdBy}
                </p>

                {selectedTicket.orderId ? (
                  <>
                    <Typography.Title level={5} style={{ marginTop: "20px" }}>
                      Order Info
                    </Typography.Title>
                    {orderLoading ? (
                      <Spin />
                    ) : orderDetails ? (
                      <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="Client">
                          {orderDetails.client?.client}
                        </Descriptions.Item>
                        <Descriptions.Item label="Order">
                          {orderDetails.orderNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="Label Count">
                          {formatNumberWithSpaces(orderDetails.labelCount)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Order Status">
                          {renderOrderStatusTag(orderDetails.orderStatus)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Machine">
                          {orderDetails.selectedProduct?.machine}
                        </Descriptions.Item>
                        <Descriptions.Item label="Knife">
                          {orderDetails.selectedProduct?.knifeCode}
                        </Descriptions.Item>
                        <Descriptions.Item label="Label Size">
                          {`${orderDetails.selectedProduct?.labelWidth || "N/A"} x ${
                            orderDetails.selectedProduct?.labelHeight || "N/A"
                          } mm`}
                        </Descriptions.Item>
                        <Descriptions.Item label="Format Width">
                          {orderDetails.selectedProduct?.formatWidth}
                        </Descriptions.Item>
                        <Descriptions.Item label="Sales Manager">
                          {orderDetails.user}
                        </Descriptions.Item>
                      </Descriptions>
                    ) : (
                      <Text type="secondary">Could not load order details.</Text>
                    )}
                    {paperManager && orderDetails && (
                      <>
                        <Divider />
                        <PaperManagerOrderOptions
                          order={orderDetails}
                          user={user}
                          ticket={selectedTicket}
                          setSelectedTicket={setSelectedTicket}
                        />
                      </>
                    )}
                  </>
                ) : null}

                {invoiceDetails && <InvoiceDetails invoice={invoiceDetails} />}

                <Typography.Title level={5} style={{ marginTop: "20px" }}>
                  Assigned Users
                </Typography.Title>

                <div>
                  {selectedTicket.assignedTo?.map((email) => (
                    <Tooltip key={email} title={email}>
                      <Avatar style={{ margin: "2px", backgroundColor: "#87d068" }}>
                        {email.charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  ))}
                  <Avatar style={{ margin: "2px" }}>
                    <PlusOutlined disabled={view === "Finished"} onClick={showAssignModal} />
                  </Avatar>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", margin: "auto" }}>
                <Empty description="Select a ticket to view its details." />
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Modal
        title="Assign Users to Ticket"
        open={isModalVisible}
        onOk={handleAssignUsers}
        onCancel={() => setIsModalVisible(false)}
        okText="Save Changes">
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="Select users to assign"
          value={usersToAssign}
          onChange={setUsersToAssign}
          optionFilterProp="label">
          {(allUsers || []).map((u) => (
            <Select.Option key={u.id} value={u.id} label={u.email}>
              {u.email}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  )
}
